import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Inbox, ChevronDown, ChevronUp, RefreshCw, User, Clock, ArrowRight, MapPin, Target, CheckCircle2 } from "lucide-react";
import MissionAffecteeModal from "@/components/activities/MissionAffecteeModal";

const PRIO = { Urgent: "bg-rose-100 text-rose-700 border-rose-200", Important: "bg-orange-100 text-orange-700 border-orange-200", Normal: "bg-slate-100 text-slate-600 border-slate-200" };
const PRIO_RANK = { Urgent: 0, Important: 1, Normal: 2 };
const STATUT = { Nouveau: "bg-blue-100 text-blue-700", "Pris en charge": "bg-amber-100 text-amber-700", Résolu: "bg-emerald-100 text-emerald-700" };
const NEXT = { Nouveau: "Pris en charge", "Pris en charge": "Résolu" };
const SOURCE_LABEL = { intervention: "Intervention maintenance", alerte: "Alerte", mission: "Mission", activite: "Intervention terrain", recrutement: "Demande de recrutement", support_direct: "Demande directe" };
const FILTERS = ["Tous", "Nouveau", "Pris en charge", "Résolu"];
const since = (iso) => { const d = (Date.now() - new Date(iso).getTime()) / 36e5; return d < 1 ? "à l'instant" : d < 24 ? `il y a ${Math.round(d)} h` : `il y a ${Math.round(d / 24)} j`; };
const fmtDate = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

function TicketRow({ t, onStatus, onMission, busy, testid }) {
  const next = NEXT[t.statut];
  return (
    <div className="flex flex-col md:flex-row md:items-center gap-3 p-3 rounded-xl border border-slate-100 hover:border-slate-200 bg-white" data-testid={testid}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${PRIO[t.priorite] || PRIO.Normal}`} data-testid={`${testid}-prio`}>{t.priorite}</span>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUT[t.statut] || ""}`} data-testid={`${testid}-statut`}>{t.statut}</span>
          <span className="text-[10px] text-slate-500 font-semibold">{SOURCE_LABEL[t.source_type] || t.source_type}{t.source_ref ? ` · ${t.source_ref}` : ""}</span>
        </div>
        <div className="font-semibold text-sm text-slate-900 mt-1 truncate" data-testid={`${testid}-titre`}>{t.titre}</div>
        {t.description && <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{t.description}</div>}
        <div className="flex items-center gap-3 flex-wrap text-[11px] text-slate-500 mt-1.5">
          <span className="inline-flex items-center gap-1"><User className="w-3 h-3"/> {t.source_user_name || "—"}{t.source_user_role ? ` (${t.source_user_role})` : ""}</span>
          {(t.corner || t.store_name) && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3"/> {t.corner || t.store_name}{t.dr ? ` · ${t.dr}` : ""}</span>}
          <span className="inline-flex items-center gap-1" title={fmtDate(t.created_at)}><Clock className="w-3 h-3"/> {fmtDate(t.created_at)} · {since(t.created_at)}</span>
          {t.support_person_name && <span>→ {t.support_person_name}</span>}
          {t.response_mission_id && <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold" data-testid={`${testid}-mission-done`}><CheckCircle2 className="w-3 h-3"/> Mission en réponse créée</span>}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0 flex-wrap">
        {!t.response_mission_id && t.statut !== "Résolu" && (
          <button onClick={() => onMission(t)} disabled={busy} data-testid={`${testid}-mission`}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg px-2.5 py-1.5 disabled:opacity-50 whitespace-nowrap">
            <Target className="w-3 h-3"/> Créer une mission en réponse
          </button>
        )}
        <select value={t.statut} disabled={busy} onChange={e => onStatus(t, e.target.value)} data-testid={`${testid}-select`}
                className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200">
          {["Nouveau", "Pris en charge", "Résolu"].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {next && <button onClick={() => onStatus(t, next)} disabled={busy} data-testid={`${testid}-next`}
                         className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-lg px-2.5 py-1.5 disabled:opacity-50 whitespace-nowrap">
          {next === "Résolu" ? "Résoudre" : "Prendre en charge"} <ArrowRight className="w-3 h-3"/>
        </button>}
      </div>
    </div>
  );
}

export default function SupportTicketsInbox({ service, testid = "tickets-inbox" }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("Tous");
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [missionFor, setMissionFor] = useState(null);
  const ROLE_TARGET = { mm: "mm", rt: "rt" };
  const prefill = missionFor ? {
    title: `Réponse : ${missionFor.titre}`, description: missionFor.description || "",
    targetType: ROLE_TARGET[missionFor.source_user_role] || "bm", targetId: missionFor.source_user_id,
    support_ticket_id: missionFor.id, priority: missionFor.priorite === "Urgent" ? "urgent" : missionFor.priorite === "Important" ? "important" : "normal",
  } : null;

  const load = useCallback(() => {
    setLoading(true);
    api.get("/support-tickets", { params: { service, limit: 300 } })
      .then(r => { setItems(r.data?.items || []); setCounts(r.data?.by_statut || {}); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [service]);
  useEffect(() => { load(); }, [load]);

  const onStatus = async (t, statut) => {
    if (statut === t.statut) return;
    setBusy(t.id);
    try {
      const r = await api.patch(`/support-tickets/${t.id}`, { statut });
      setItems(p => p.map(x => x.id === t.id ? { ...x, ...r.data } : x));
      setCounts(c => ({ ...c, [t.statut]: Math.max(0, (c[t.statut] || 0) - 1), [statut]: (c[statut] || 0) + 1 }));
      toast.success(`Ticket → ${statut}`);
    } catch { toast.error("Mise à jour impossible"); }
    setBusy(null);
  };

  const shown = items
    .filter(t => filter === "Tous" || t.statut === filter)
    .sort((a, b) => (PRIO_RANK[a.priorite] ?? 9) - (PRIO_RANK[b.priorite] ?? 9) || b.created_at.localeCompare(a.created_at));
  const openCount = (counts.Nouveau || 0) + (counts["Pris en charge"] || 0);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm" data-testid={testid}>
      <div className="flex items-center gap-3 px-4 py-3">
        <button onClick={() => setOpen(o => !o)} className="flex items-center gap-2 flex-1 text-left" data-testid={`${testid}-toggle`}>
          <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 grid place-items-center relative">
            <Inbox className="w-4 h-4"/>
            {openCount > 0 && <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold grid place-items-center" data-testid={`${testid}-badge`}>{openCount}</span>}
          </div>
          <div>
            <h2 className="font-display text-base font-bold text-slate-900 leading-tight">Demandes reçues <span className="text-slate-400 font-normal text-xs">· service {service}</span></h2>
            <p className="text-[11px] text-slate-500">{counts.Nouveau || 0} nouvelle{(counts.Nouveau || 0) > 1 ? "s" : ""} · {counts["Pris en charge"] || 0} en cours · {counts.Résolu || 0} résolue{(counts.Résolu || 0) > 1 ? "s" : ""}</p>
          </div>
          {open ? <ChevronUp className="w-4 h-4 text-slate-400 ml-1"/> : <ChevronDown className="w-4 h-4 text-slate-400 ml-1"/>}
        </button>
        <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5">
          {FILTERS.map(f => <button key={f} onClick={() => { setFilter(f); setOpen(true); }} data-testid={`${testid}-filter-${f.replace(/\s/g, "-").toLowerCase()}`}
                                    className={`text-[11px] font-semibold px-2.5 py-1 rounded-md ${filter === f ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>
            {f}{f !== "Tous" && counts[f] ? <span className="ml-1 text-[9px] text-slate-400">{counts[f]}</span> : null}
          </button>)}
        </div>
        <button onClick={load} disabled={loading} data-testid={`${testid}-refresh`} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100 text-slate-500"><RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}/></button>
      </div>
      {open && (
        <div className="px-4 pb-4 space-y-2 max-h-[420px] overflow-y-auto">
          {shown.length === 0 && <p className="text-xs text-slate-400 text-center py-6" data-testid={`${testid}-empty`}>{loading ? "Chargement…" : "Aucune demande pour ce filtre."}</p>}
          {shown.map(t => <TicketRow key={t.id} t={t} onStatus={onStatus} onMission={setMissionFor} busy={busy === t.id} testid={`${testid}-row-${t.id}`}/>)}
        </div>
      )}
      <MissionAffecteeModal open={!!missionFor} prefill={prefill} onClose={() => setMissionFor(null)} onSaved={() => { setMissionFor(null); load(); }}/>
    </div>
  );
}
